/* CategoryPage updated to the restrained Buitn Planten catalogue card style.  Top spacing now follows the responsive header spacer.
   Updated: Added DataFast category_viewed goal on page load. */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { supabase } from '@/lib/supabase';
import { calculatePriceInclBTW } from '@/lib/btw-calculator';
import { getProductImageUrl } from '@/lib/image-url';
import ContactBar from '@/components/ContactBar';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PageBreadcrumb from '@/components/PageBreadcrumb';
import { Loader as Loader2, Search, Package, ArrowRight } from 'lucide-react';
import EmptyState from '@/components/EmptyState';
import { trackGoal } from '@/lib/datafast';

interface ProductCard {
  id: string;
  name: string;
  scientific_name: string;
  base_slug: string;
  geslacht: string;
  sort: string;
  subcategory_id: string | null;
  image_url: string | null;
  lowest_price_incl_btw: number;
  variant_count: number;
  types: string[];
  btw_percentage: number;
}

interface Subcategory {
  id: string;
  name: string;
  slug: string;
  display_order: number;
  thumbnail_path: string | null;
}

const CATEGORY_META: Record<string, { title: string; description: string }> = {
  haagplanten: {
    title: 'Haagplanten',
    description: 'Ontdek ons uitgebreide assortiment haagplanten. Van laurier tot hulst, wij hebben de perfecte haagplant voor uw tuin.',
  },
  bomen: {
    title: 'Bomen',
    description: 'Bekijk onze selectie bomen. Van sierlijke sierbomen tot robuuste laanbomen voor uw tuin of erf.',
  },
  tuinplanten: {
    title: 'Tuinplanten',
    description: 'Verken onze collectie tuinplanten. Van bloeiers tot groenblijvende struiken voor elke tuin.',
  },
};

export default function CategoryPage({ category }: { category: string }) {
  const [products, setProducts] = useState<ProductCard[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const meta = CATEGORY_META[category] || { title: category, description: '' };

  useEffect(() => {
    setSearchQuery('');
    setProducts([]);
    setSubcategories([]);
    fetchData();
  }, [category]);

  useEffect(() => {
    trackGoal('category_viewed', { category });
  }, [category]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const { data: catRow } = await supabase
        .from('product_categories')
        .select('id')
        .eq('slug', category)
        .single();

      if (!catRow) return;

      const { data: subData } = await supabase
        .from('product_subcategories')
        .select('id, name, slug, display_order, thumbnail_path')
        .eq('category_id', catRow.id)
        .eq('active', true)
        .order('name');

      if (subData && subData.length > 0) {
        const subsWithProducts = await Promise.all(
          subData.map(async (sub) => {
            const { count } = await supabase
              .from('products')
              .select('id', { count: 'exact', head: true })
              .eq('subcategory_id', sub.id)
              .eq('active', true);
            return { ...sub, _productCount: count || 0 };
          })
        );

        const visibleSubs = subsWithProducts
          .filter(s => s._productCount > 0)
          .map(({ _productCount, ...rest }) => rest);

        if (visibleSubs.length > 0) {
          setSubcategories(visibleSubs);
          setLoading(false);
          return;
        }
      }

      // No visible subcategories — load products directly
      await fetchProducts(catRow.id);
    } catch (err) {
      console.error('Error fetching category data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async (categoryId: string) => {
    const { data: productsData, error } = await supabase
      .from('products')
      .select(`
        id,
        name,
        scientific_name,
        base_slug,
        geslacht,
        sort,
        subcategory_id,
        btw_percentage,
        is_simple_product
      `)
      .eq('active', true)
      .eq('is_simple_product', false)
      .eq('category_id', categoryId)
      .order('name');

    if (error) throw error;
    if (!productsData || productsData.length === 0) return;

    const productCards: ProductCard[] = await (async () => {
      const productIds = productsData.map((p: any) => p.id);

      const [variantsResult, imagesResult] = await Promise.all([
        productIds.length
          ? supabase
              .from('product_variants')
              .select('product_id, price_tier_1, type')
              .in('product_id', productIds)
              .eq('active', true)
          : Promise.resolve({ data: [] as any[] }),
        productIds.length
          ? supabase
              .from('product_images')
              .select('product_id, storage_path, uses_new_bucket')
              .in('product_id', productIds)
              .is('variant_id', null)
              .eq('is_primary', true)
          : Promise.resolve({ data: [] as any[] }),
      ]);

      const variantsByProduct = new Map<string, any[]>();
      (variantsResult.data || []).forEach((v: any) => {
        if (!variantsByProduct.has(v.product_id)) variantsByProduct.set(v.product_id, []);
        variantsByProduct.get(v.product_id)!.push(v);
      });
      const primaryImageByProduct = new Map<string, any>();
      (imagesResult.data || []).forEach((img: any) => {
        if (!primaryImageByProduct.has(img.product_id)) primaryImageByProduct.set(img.product_id, img);
      });

      return productsData.map((p: any) => {
        const variants = variantsByProduct.get(p.id) || [];
        const prices = variants
          .map((v: any) => parseFloat(v.price_tier_1 || '0'))
          .filter((pr: number) => pr > 0);
        const lowestPrice = prices.length > 0 ? Math.min(...prices) : 0;
        const btwPercentage = p.btw_percentage || 9;
        const lowestPriceInclBtw = calculatePriceInclBTW(lowestPrice, btwPercentage);
        const types = Array.from(new Set(variants.map((v: any) => v.type)));

        const imageData = primaryImageByProduct.get(p.id);
        const imageUrl = imageData
          ? getProductImageUrl(imageData.storage_path, imageData.uses_new_bucket)
          : null;

        return {
          id: p.id,
          name: p.name,
          scientific_name: p.scientific_name || '',
          base_slug: p.base_slug,
          geslacht: p.geslacht || '',
          sort: p.sort || '',
          subcategory_id: p.subcategory_id || null,
          image_url: imageUrl,
          lowest_price_incl_btw: lowestPriceInclBtw,
          variant_count: variants.length,
          types,
          btw_percentage: btwPercentage,
        };
      });
    })();

    setProducts(productCards);
  };

  const filteredProducts = products.filter(p =>
    !searchQuery.trim() ||
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.scientific_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.geslacht.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const canonicalUrl = `https://buitnplanten.nl/${category}`;

  if (loading) {
    return (
      <>
        <Helmet>
          <title>{meta.title} - Buitn Planten</title>
          <meta name="description" content={meta.description} />
          <link rel="canonical" href={canonicalUrl} />
        </Helmet>
        <ContactBar />
        <div className="min-h-screen flex items-center justify-center bg-[#fcfaf6] pt-0">
          <Loader2 className="h-12 w-12 animate-spin text-[#1e2b22]" />
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{meta.title} - Buitn Planten</title>
        <meta name="description" content={meta.description} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={`${meta.title} - Buitn Planten`} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={canonicalUrl} />

        {/* BreadcrumbList Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://buitnplanten.nl/" },
              { "@type": "ListItem", "position": 2, "name": meta.title, "item": canonicalUrl }
            ]
          })}
        </script>

        {/* CollectionPage Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": meta.title,
            "description": meta.description,
            "url": canonicalUrl,
            "isPartOf": {
              "@type": "WebSite",
              "name": "Buitn Planten",
              "url": "https://buitnplanten.nl/"
            },
            "mainEntity": {
              "@type": "ItemList",
              "itemListElement": (subcategories.length > 0 ? subcategories : filteredProducts).map((item, index) => ({
                "@type": "ListItem",
                "position": index + 1,
                "url": subcategories.length > 0
                  ? `https://buitnplanten.nl/${category}/${item.slug}`
                  : `https://buitnplanten.nl/${item.base_slug}`,
                "name": item.name
              }))
            }
          })}
        </script>
      </Helmet>
      <ContactBar />
      <div className="min-h-screen bg-[#fcfaf6]">
        <Header />

        <main className="mx-auto max-w-[1240px] px-5 lg:px-8 pt-8 pb-16">
          <PageBreadcrumb items={[{ label: 'Home', href: '/' }, { label: meta.title }]} />
          <div className="mb-12">
            <p className="mb-3 text-xs uppercase tracking-[0.18em] text-[#5a6552]">Onze collectie</p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl text-[#1e2b22] mb-4">{meta.title}</h1>
            <p className="text-base md:text-lg text-[#5a6552] max-w-2xl leading-relaxed">{meta.description}</p>
          </div>

          {/* Subcategory cards */}
          {subcategories.length > 0 && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
              {subcategories.map((sub, index) => (
                <motion.div key={sub.id} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: index * 0.06 }} className="h-full">
                  <Link to={`/${category}/${sub.slug}`} className="group h-full flex flex-col rounded-xl overflow-hidden border border-transparent hover:ring-2 hover:ring-buitn-bosgroen transition-all duration-200 bg-white" style={{ ['--tw-ring-offset-color' as any]: '#fcfaf6', ['--tw-ring-offset-width' as any]: '3px' }}>
                    <div className="aspect-[4/3] overflow-hidden">
                      {sub.thumbnail_path ? (
                        <img src={getProductImageUrl(sub.thumbnail_path)} alt={sub.name} width={400} height={300} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-[#e7e1d6]"><Package className="h-10 w-10 text-[#cfcac2]" /></div>
                      )}
                    </div>
                    <div className="flex-1 px-3 py-2.5 bg-white border-t border-gray-100 flex items-center justify-between gap-1">
                      <span className="text-xs sm:text-[13px] font-medium text-buitn-bosgroen leading-snug break-words">{sub.name}</span>
                      <ArrowRight className="w-3 h-3 flex-shrink-0 text-buitn-bosgroen/50 group-hover:text-buitn-bosgroen transition-colors" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}

          {/* Direct product grid (no subcategories) */}
          {subcategories.length === 0 && (
            <>
              {products.length > 3 && (
                <div className="mb-8 max-w-md">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#cfcac2]" />
                    <input type="text" placeholder="Zoek op naam..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full rounded-[5px] border border-[#cfcac2] bg-[#fcfaf6] py-3 pl-10 pr-4 text-sm text-[#1a1a1a] outline-none focus:border-[#5a6552]" />
                  </div>
                </div>
              )}

              {filteredProducts.length === 0 ? (
                <EmptyState
                  message={searchQuery ? 'Geen producten gevonden.' : 'Er zijn nog geen producten in deze categorie.'}
                  actionLabel={searchQuery ? 'Zoeken wissen' : undefined}
                  onAction={searchQuery ? () => setSearchQuery('') : undefined}
                />
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-5">
                  {filteredProducts.map((product, index) => (
                    <Link key={product.id} to={`/${product.base_slug}`} className="group flex flex-col rounded-xl overflow-hidden border border-transparent hover:ring-2 hover:ring-buitn-bosgroen transition-all duration-200 bg-white" style={{ ['--tw-ring-offset-color' as any]: '#fcfaf6', ['--tw-ring-offset-width' as any]: '3px' }}>
                      <div className="aspect-[4/3] overflow-hidden relative">
                        {product.image_url ? (
                          <img src={product.image_url} alt={product.name} width={400} height={300} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-[#e7e1d6]"><Package className="h-10 w-10 text-[#cfcac2]" /><span className="text-xs text-[#cfcac2]">Foto volgt</span></div>
                        )}
                        {product.lowest_price_incl_btw > 0 && (
                          <div className="absolute right-3 top-3 rounded-[4px] bg-[#1e2b22] px-2.5 py-1 text-xs font-medium text-[#e7e1d6]">Vanaf €{product.lowest_price_incl_btw.toFixed(2)}</div>
                        )}
                      </div>
                      <div className="flex-1 px-3 py-2.5 bg-white border-t border-gray-100 flex items-center justify-between gap-1">
                        <div className="min-w-0 flex-1">
                          {product.scientific_name && <p className="mb-0.5 line-clamp-1 text-[11px] italic text-[#cfcac2]">{product.scientific_name}</p>}
                          <span className="line-clamp-2 text-xs sm:text-[13px] font-medium leading-tight text-buitn-bosgroen">{product.name}</span>
                        </div>
                        <ArrowRight className="w-3 h-3 flex-shrink-0 text-buitn-bosgroen/50 group-hover:text-buitn-bosgroen transition-colors" />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}
